import {
  ChevronLeft,
  CircleDollarSign,
  LayoutPanelTop,
  LockKeyhole,
  Newspaper,
  Palette,
  PencilRuler,
  Split,
  Tag,
  Truck,
} from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDialog,
  IgPaper,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { TabEnumFe } from "@intelligems/ig-types";
import { checkOfferLimit } from "@intelligems/ig-utils/offer";
import { Grid, useTheme } from "@mui/material";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useCanCreateAdvancedContentTest } from "@src/components/LimitAccess/check-experience-access-hooks";
import {
  checkAllScopes,
  checkAnyScope,
  checkBooleanScope,
} from "@src/components/LimitAccess/check-scopes";
import { ScopedComponentWithDisable } from "@src/components/LimitAccess/ScopedComponentWithDisable";
import { EXPERIMENT_ROUTES, SETTINGS_ROUTES } from "@src/routes/routes";
import { getDialog, setDialog } from "@src/store/slices/global";
import { theme } from "@src/theme";
import { ExperienceTypeListItem } from "@src/types/experience";

const experienceTypeList: ExperienceTypeListItem[] = [
  {
    key: "pricing_experiment",
    name: "Pricing Test",
    features: ["create_price_experiment"],
    enabledIcon: <CircleDollarSign size={22} />,
    description:
      "Test the price of one or multiple products in your Shopify store.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=pricing&tab=${TabEnumFe.TEST_GROUPS}`,
  },
  {
    key: "shipping_experiment",
    name: "Shipping Test",
    features: ["create_shipping_experiment"],
    enabledIcon: <Truck size={22} />,
    description:
      "Test the rates you charge for shipping, including free thresholds.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=shipping&tab=${TabEnumFe.TEST_GROUPS}`,
  },
  {
    key: "content_experiment",
    name: "Content Test",
    features: [
      "create_price_experiment",
      "create_redirect_experiment",
      "create_onsite_edits_experiment",
      "create_template_experiment",
    ],
    enabledIcon: <Newspaper size={22} />,
    description: "Test the display contents on your Shopify store.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&tab=${TabEnumFe.TEST_GROUPS}`,
  },
  {
    key: "volume_discount_experiment",
    name: "Offers Test",
    features: ["create_discount_experiment", "create_offer"],
    enabledIcon: <Tag size={22} />,
    description: "Test different offers on your Shopify store.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=personalization/offer&tab=${TabEnumFe.TEST_GROUPS}`,
  },
];

const contentExperienceTypeList: ExperienceTypeListItem[] = [
  {
    key: "split_test",
    name: "Split URL Test",
    features: ["create_onsite_edits_experiment", "create_redirect_experiment"],
    enabledIcon: <Split size={22} />,
    description: "Test redirecting users to various site pages.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=content/url&tab=${TabEnumFe.TEST_GROUPS}`,
  },
  {
    key: "onsite_test",
    name: "Onsite Edits",
    features: [
      "create_onsite_edits_experiment",
      "create_onsite_edits_experiment",
    ],
    enabledIcon: <PencilRuler size={22} />,
    description: "Test any element of your online store.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=content/onsiteEdits&tab=${TabEnumFe.TEST_GROUPS}`,
  },
  {
    key: "template_test",
    name: "Template Test",
    features: ["create_onsite_edits_experiment", "create_template_experiment"],
    enabledIcon: <LayoutPanelTop size={22} />,
    description: "Compare and test different Shopify templates.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=content/template&tab=${TabEnumFe.TEST_GROUPS}`,
  },
  {
    key: "theme_test",
    name: "Theme Test",
    features: ["create_onsite_edits_experiment", "create_theme_experiment"],
    enabledIcon: <Palette size={22} />,
    description: "Compare and test different Shopify themes.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=content/theme&tab=${TabEnumFe.TEST_GROUPS}`,
  },
  {
    key: "advanced_test",
    name: "Advanced",
    features: [
      "create_redirect_experiment",
      "create_theme_experiment",
      "create_template_experiment",
      "create_onsite_edits_experiment",
    ],
    enabledIcon: <Newspaper size={22} />,
    description: "Customize every element of your content test.",
    link: `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=content/advanced&tab=${TabEnumFe.TEST_GROUPS}`,
  },
];

interface ExperienceTypeButtonProps {
  experienceType: ExperienceTypeListItem;
  selectedTestType: ExperienceTypeListItem | undefined;
  isDisabled?: boolean;
  onClick: (
    experienceType: ExperienceTypeListItem,
    isDisabled?: boolean
  ) => void;
}

const ExperienceTypeButton = ({
  experienceType,
  selectedTestType,
  isDisabled,
  onClick,
}: ExperienceTypeButtonProps) => {
  const theme = useTheme();
  return (
    <Grid item>
      <IgPaper
        onClick={() => {
          onClick(experienceType, isDisabled);
        }}
        isClickable={true}
        isSelected={selectedTestType?.key === experienceType.key}
        isDisabled={isDisabled}
        sx={{ padding: "16px 20px", minHeight: "70px" }}
      >
        <Grid
          item
          container
          direction={"column"}
          textAlign={"start"}
          rowSpacing={1}
        >
          <Grid item container direction={"row"} columnSpacing={2}>
            <Grid item>
              {isDisabled ? (
                <LockKeyhole size={22} />
              ) : (
                experienceType.enabledIcon
              )}
            </Grid>
            <Grid item>
              <IgTypography color={theme.palette.text.secondary}>
                {experienceType.name}
              </IgTypography>
            </Grid>
          </Grid>
          <Grid item>
            <IgTypography
              color={theme.palette.text.secondary}
              variant={"body2"}
              sx={{
                width: "150px",
              }}
            >
              {experienceType.description}
            </IgTypography>
          </Grid>
        </Grid>
      </IgPaper>
    </Grid>
  );
};

export const CreateExperimentDialog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const organization = useGetOrg();
  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const personalizationsList = useGetExperiencesList({
    category: "personalization",
  });

  const dialog = useSelector(getDialog("createExperiment"));
  const isAdmin = useIsAdmin();
  const [selectedTestType, setSelectedTestType] = useImmer<
    ExperienceTypeListItem | undefined
  >(undefined);
  const canCreateAdvancedContentTest = useCanCreateAdvancedContentTest();
  const [showContentSelection, setShowContentSelection] =
    useImmer<boolean>(false);
  const [showAdvancedTest, setShowAdvancedTest] = useImmer<boolean>(false);

  const [details, setDetails] = useImmer<{
    name: string | null;
    description: string | null;
  }>({ name: "", description: null });

  const filterKeys = dialog.props?.filterKeys || [];
  const hideBackButton = dialog.props?.hideBackButton || false;

  const handleClose = () => {
    dispatch(
      setDialog({ dialog: "createExperiment", open: false, props: undefined })
    );
    setSelectedTestType(undefined);
  };

  const doesHaveAccess = () => {
    const featureAccess: boolean[] = [];
    experienceTypeList.forEach((type) => {
      const featureTypeAccess: boolean[] = [];
      type.features.forEach((feature) => {
        featureTypeAccess.push(
          checkBooleanScope(feature, organization.data.org.configurations)
        );
      });
      featureAccess.push(checkAllScopes(featureTypeAccess));
    });
    return isAdmin || featureAccess.every((feature) => feature);
  };

  const onCreateClick = () => {
    if (!selectedTestType) return;

    const navigateToSelectedTest = () => {
      handleClose();
      void navigate(selectedTestType.link, {
        state: {
          name: details.name,
          description: details.description,
        },
      });
    };
    if (selectedTestType.key === "content_experiment") {
      setShowContentSelection(true);
    } else if (selectedTestType.key === "volume_discount_experiment") {
      const isUnderLimit = checkOfferLimit(
        organization.data.org,
        personalizationsList.data?.experiencesList,
        experiencesList.data?.experiencesList
      );

      if (!isUnderLimit) {
        dispatch(setDialog({ dialog: "offerLimitReached", open: true }));
        dispatch(setDialog({ dialog: "createExperiment", open: false }));
        return;
      }
      navigateToSelectedTest();
    } else {
      navigateToSelectedTest();
    }
  };
  const items = useMemo(() => {
    const _items = showContentSelection
      ? showAdvancedTest
        ? contentExperienceTypeList
        : contentExperienceTypeList.filter(
            (test) => test.key !== "advanced_test"
          )
      : experienceTypeList;

    if (!filterKeys.length) {
      return _items;
    }

    return _items.filter((i) => filterKeys.includes(i.key));
  }, [filterKeys.length, showContentSelection, showAdvancedTest]);

  const canCreateShippingTest = useMemo(() => {
    if (experiencesList.isSuccess) {
      return (
        experiencesList.data.experiencesList.filter(
          (exp) => exp.testTypes.hasTestShipping && exp.status !== "ended"
        ).length === 0
      );
    } else return true;
  }, [experiencesList]);

  const onClick = (
    experienceType: ExperienceTypeListItem,
    isDisabled?: boolean
  ) => {
    if (!isDisabled) {
      setSelectedTestType(experienceType);
      if (experienceType.key === "content_experiment") {
        if (canCreateAdvancedContentTest) {
          setShowAdvancedTest(true);
        }
        setShowContentSelection(true);
      }
    }
  };

  useEffect(() => {
    setShowContentSelection(dialog.props?.showContentSection || false);
  }, [dialog.props?.showContentSection]);

  if (experiencesList.isSuccess && dialog.isOpen) {
    return (
      <IgDialog
        dontUseDialogContent={true}
        // dialogContentWidth={"1000px"}
        // minWidth={"500px"}
        fullWidth={true}
        maxWidth={"sm"}
        showCloseIcon={true}
        hideActions={true}
        dialogHeight={null}
        padding={"22px 25px"}
        handleClose={() =>
          dispatch(setDialog({ dialog: "createExperiment", open: false }))
        }
        title={""}
        showDialog={dialog.isOpen}
        rawContent={
          <Grid container direction={"column"} rowSpacing={2}>
            <Grid item>
              <IgTypography
                color={theme.palette.text.secondary}
                sx={{
                  fontWeight: 600,
                }}
                variant={"h3"}
              >
                Create A New Test
              </IgTypography>
            </Grid>
            <Grid item>
              <IgTextField
                fullWidth={true}
                placeholder={"Enter the name of your test"}
                label={"Name"}
                variant={"outlined"}
                value={details.name ?? ""}
                onChange={(e) => {
                  setDetails((draft) => {
                    draft.name = e.target.value;
                    return draft;
                  });
                }}
              />
            </Grid>
            <Grid item>
              <IgTextField
                fullWidth={true}
                placeholder={
                  "Describe purpose or hypothesis to aid with collaboration and planning"
                }
                label={"Description"}
                variant={"outlined"}
                value={details.description ?? ""}
                onChange={(e) => {
                  setDetails((draft) => {
                    draft.description = e.target.value;
                    return draft;
                  });
                }}
              />
            </Grid>
            <Grid
              item
              container
              direction={"row"}
              columnSpacing={2}
              rowSpacing={2}
            >
              {items.map((test) => (
                <Grid item key={test.key} xs={6}>
                  <ScopedComponentWithDisable
                    adminDisable={!canCreateShippingTest}
                    requiredShopifyScopes={
                      test.key === "shipping_experiment"
                        ? ["read_locations"]
                        : []
                    }
                    isEnabled={
                      test.key === "shipping_experiment"
                        ? checkAllScopes([
                            checkAnyScope(
                              test.features.map((feature) =>
                                checkBooleanScope(
                                  feature,
                                  organization.data.org.configurations
                                )
                              )
                            ),
                            canCreateShippingTest,
                          ])
                        : test.key === "volume_discount_experiment"
                          ? checkAllScopes(
                              test.features.map((feature) =>
                                checkBooleanScope(
                                  feature,
                                  organization.data.org.configurations
                                )
                              )
                            )
                          : checkAnyScope(
                              test.features.map((feature) =>
                                checkBooleanScope(
                                  feature,
                                  organization.data.org.configurations
                                )
                              )
                            )
                    }
                    customDisabledTooltipMessage={
                      test.key === "shipping_experiment" &&
                      !canCreateShippingTest
                        ? "Please delete or end your existing shipping test."
                        : "Please upgrade your plan to unlock this experiment."
                    }
                    debugId={`create-${test.key}-experiment-modal-button`}
                    customComponentWhenDisabled={
                      <ExperienceTypeButton
                        experienceType={test}
                        isDisabled={true}
                        selectedTestType={selectedTestType}
                        onClick={onClick}
                      />
                    }
                  >
                    <ExperienceTypeButton
                      experienceType={test}
                      selectedTestType={selectedTestType}
                      onClick={onClick}
                    />
                  </ScopedComponentWithDisable>
                </Grid>
              ))}
            </Grid>
            <Grid
              item
              container
              direction={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              rowSpacing={2}
              sx={{ marginTop: "10px" }}
            >
              {!doesHaveAccess() && (
                <Grid item>
                  <IgTypography variant={"body2"}>
                    <IgTypography
                      igStyle={"linkNoBold"}
                      variant={"body2"}
                      onClick={() => {
                        void navigate(SETTINGS_ROUTES.Settings);
                        handleClose();
                      }}
                    >
                      Upgrade your plan{" "}
                    </IgTypography>
                    if you're interested in unlocking these tests.
                  </IgTypography>
                </Grid>
              )}
              <Grid
                item
                container
                direction={"row"}
                justifyContent={"space-between"}
              >
                <Grid item xs={1}>
                  {showContentSelection && !hideBackButton && (
                    <IgButton
                      variant={"text"}
                      onClick={() => {
                        setSelectedTestType(undefined);
                        setShowContentSelection(false);
                        setShowAdvancedTest(false);
                      }}
                      startIcon={<ChevronLeft size={16} />}
                      color={"secondary"}
                    >
                      Back
                    </IgButton>
                  )}
                </Grid>

                <Grid item>
                  <IgTooltip
                    title={
                      !selectedTestType ||
                      (selectedTestType.key === "content_experiment" &&
                        showContentSelection)
                        ? "Select a test type before continuing"
                        : !details.name
                          ? "Give your test a name before continuing"
                          : ""
                    }
                  >
                    <IgButton
                      onClick={onCreateClick}
                      disabled={
                        !selectedTestType ||
                        (selectedTestType.key === "content_experiment" &&
                          showContentSelection) ||
                        !details.name
                      }
                      variant={"contained"}
                    >
                      Create Test
                    </IgButton>
                  </IgTooltip>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        }
      />
    );
  }
  return null;
};
